import { EnvelopeClosedIcon } from "@radix-ui/react-icons";
import { Box, Button, Flex, Separator, Tabs } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import { usePostHog } from "../../hooks/usePostHog";
import SignInForm from "./SignInForm";
import SignUpForm from "./SignUpForm";

interface AuthFormProps {
    onSignIn: (data: any) => Promise<void>;
    onSignUp: (data: any) => Promise<void>;
    onGoogleSignIn: () => void;
    loading: boolean;
    onForgotPassword?: () => void;
}

function AuthForm({ onSignIn, onSignUp, onGoogleSignIn, loading, onForgotPassword }: AuthFormProps) {
    const { track } = usePostHog();
    const hasTrackedPageView = useRef(false);
    // Email/password is collapsed by default; revealed on demand (progressive disclosure).
    const [showEmail, setShowEmail] = useState(false);

    // Track page view on mount (only once)
    useEffect(() => {
        if (!hasTrackedPageView.current) {
            hasTrackedPageView.current = true;
            track("page_auth_form_viewed", {
                timestamp: new Date().toISOString(),
            });
        }
    }, [
        track,
    ]);

    return (
        <Box width="100%">
            {/* Primary CTA: Google is the main, highest-emphasis action. */}
            <Button
                size="3"
                variant="solid"
                highContrast
                style={{
                    width: "100%",
                    cursor: "pointer",
                }}
                onClick={onGoogleSignIn}
                disabled={loading}
            >
                <svg
                    className="google-icon"
                    viewBox="0 0 24 24"
                    style={{
                        width: 18,
                        height: 18,
                        marginRight: 8,
                    }}
                    aria-labelledby="google-title"
                >
                    <title id="google-title">Google</title>
                    {/* Monochrome white "G" — matches the button text, no color clash with the blue.
              (Google brand guidelines: use the white logo on colored/dark buttons.) */}
                    <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                </svg>
                Continue with Google
            </Button>

            <Flex direction="row" align="center" gap="3" my="4">
                <Separator
                    size="4"
                    style={{
                        flex: 1,
                    }}
                />
                <Button
                    variant="ghost"
                    disabled
                    style={{
                        color: "var(--gray-a11)",
                    }}
                >
                    OR
                </Button>
                <Separator
                    size="4"
                    style={{
                        flex: 1,
                    }}
                />
            </Flex>

            {/* Secondary path: email/password collapsed by default behind a single button,
          keeping Google the obvious primary choice. */}
            {showEmail ? (
                <Tabs.Root defaultValue="signin">
                    <Tabs.List size="2">
                        <Tabs.Trigger
                            value="signin"
                            style={{
                                flex: 1,
                            }}
                        >
                            Sign In
                        </Tabs.Trigger>
                        <Tabs.Trigger
                            value="signup"
                            style={{
                                flex: 1,
                            }}
                        >
                            Sign Up
                        </Tabs.Trigger>
                    </Tabs.List>

                    <Box pt="4">
                        <Tabs.Content value="signin">
                            <SignInForm onSubmit={onSignIn} loading={loading} onForgotPassword={onForgotPassword} />
                        </Tabs.Content>
                        <Tabs.Content value="signup">
                            <SignUpForm onSubmit={onSignUp} loading={loading} />
                        </Tabs.Content>
                    </Box>
                </Tabs.Root>
            ) : (
                <Button
                    size="3"
                    variant="soft"
                    color="gray"
                    style={{
                        width: "100%",
                        cursor: "pointer",
                    }}
                    onClick={() => setShowEmail(true)}
                >
                    <EnvelopeClosedIcon />
                    Continue with email
                </Button>
            )}
        </Box>
    );
}

export default AuthForm;
